import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { Palette, Upload, Image, Video, MessageSquare, Save, X, Check, Trash2, RotateCcw } from "lucide-react";
import organizationsService from "../../services/organizationsService";
import AuthContext from "../../context/AuthContext";
import { getApexHost } from "../../context/TenantContext";
import { useTranslation } from "../../i18n";
import BannerMedia from "../BannerMedia";

// Display-only suffix shown next to the subdomain input. Derived from the host
// the admin panel is currently served on, so production (addflint.com) shows
// ".addflint.com" while demo/staging (addflint.io) shows ".addflint.io". Falls
// back to ".addflint.io" on localhost / Vercel preview hosts where the apex
// isn't a real brand domain. The stored value is only the slug, never the TLD.
const getDomainSuffix = () => {
  const apex = getApexHost();
  if (!apex || apex === "localhost" || apex.endsWith("vercel.app")) {
    return "addflint.io";
  }
  return apex;
};

const DEFAULT_PRIMARY = "#7C3AED";
const DEFAULT_SECONDARY = "#4F46E5";
const DEFAULT_RECOGNITION = "#7C3AED";
const DEFAULT_NOMINATION = "#F97316";


// Mirrors the backend check: banner videos must be absolute https links.
const isHttpsUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && Boolean(url.hostname) && !/\s/.test(value);
  } catch {
    return false;
  }
};

const CustomizationSection = () => {
  const { t } = useTranslation("admin");
  const { refreshUser } = useContext(AuthContext);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Customization form state
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY);
  const [welcomeTitle, setWelcomeTitle] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [recognitionColor, setRecognitionColor] = useState(DEFAULT_RECOGNITION);
  const [nominationColor, setNominationColor] = useState(DEFAULT_NOMINATION);
  const [bannerPosition, setBannerPosition] = useState(50);
  const [bannerZoom, setBannerZoom] = useState(100);
  const [logoPreview, setLogoPreview] = useState(null);
  const [widgetLogoPreview, setWidgetLogoPreview] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [bannerVideoUrl, setBannerVideoUrl] = useState("");
  const [appName, setAppName] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [savingSubdomain, setSavingSubdomain] = useState(false);
  const [subdomainError, setSubdomainError] = useState("");
  const [avatarColor, setAvatarColor] = useState("");
  const [widgetButtonBg, setWidgetButtonBg] = useState("");
  const [widgetPanelBg, setWidgetPanelBg] = useState("");
  const [widgetHeaderBg, setWidgetHeaderBg] = useState("");
  const [widgetHeaderText, setWidgetHeaderText] = useState("");
  const [widgetNavBg, setWidgetNavBg] = useState("");
  const [widgetCardBg, setWidgetCardBg] = useState("");
  const [widgetNameColor, setWidgetNameColor] = useState("");
  const [loginLogoPreview, setLoginLogoPreview] = useState(null);
  const [navConfig, setNavConfig] = useState({
    home: { icon: "🏠", label: "Home" },
    recognitions: { icon: "⭐", label: "Recognitions" },
    nominations: { icon: "🏆", label: "Nominations" },
    marketplace: { icon: "🎁", label: "Shop" },
  });

  // Display names for the widget tab keys (the keys themselves are config).
  const widgetTabNames = {
    home: t("customization.widget_tabs.home"),
    recognitions: t("customization.widget_tabs.recognitions"),
    nominations: t("customization.widget_tabs.nominations"),
    marketplace: t("customization.widget_tabs.marketplace"),
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const orgs = await organizationsService.list();
      setOrganizations(Array.isArray(orgs) ? orgs : orgs?.data || []);
    } catch {
      setError(t("customization.errors.load_organizations"));
    } finally {
      setLoading(false);
    }
  };

  const selectOrg = (org) => {
    setSelectedOrg(org);
    setPrimaryColor(org.primary_color || DEFAULT_PRIMARY);
    setSecondaryColor(org.secondary_color || DEFAULT_SECONDARY);
    setWelcomeTitle(org.welcome_title || "");
    setWelcomeMessage(org.welcome_message || "");
    setBannerPosition(org.banner_position ?? 50);
    setBannerZoom(org.banner_zoom ?? 100);
    setRecognitionColor(org.recognition_color || DEFAULT_RECOGNITION);
    setNominationColor(org.nomination_color || DEFAULT_NOMINATION);
    setLogoPreview(org.logo_url || null);
    setWidgetLogoPreview(org.widget_logo_url || null);
    setBgPreview(org.background_image_url || null);
    setBannerVideoUrl(org.banner_video_url || "");
    setAppName(org.app_name || "");
    setFaviconUrl(org.favicon_url || "");
    setSubdomain(org.subdomain || "");
    setSubdomainError("");
    setLoginLogoPreview(org.login_logo_url || null);
    setAvatarColor(org.avatar_color || "");
    setWidgetButtonBg(org.widget_button_bg || "");
    setWidgetPanelBg(org.widget_panel_bg || "");
    setWidgetHeaderBg(org.widget_header_bg || "");
    setWidgetHeaderText(org.widget_header_text || "");
    setWidgetNavBg(org.widget_nav_bg || "");
    setWidgetCardBg(org.widget_card_bg || "");
    setWidgetNameColor(org.widget_name_color || "");
    try {
      const parsed = org.widget_nav_config ? JSON.parse(org.widget_nav_config) : null;
      setNavConfig(parsed || {
        home: { icon: "🏠", label: "Home" },
        recognitions: { icon: "⭐", label: "Recognitions" },
        nominations: { icon: "🏆", label: "Nominations" },
        marketplace: { icon: "🎁", label: "Shop" },
      });
    } catch { setNavConfig({ home: { icon: "🏠", label: "Home" }, recognitions: { icon: "⭐", label: "Recognitions" }, nominations: { icon: "🏆", label: "Nominations" }, marketplace: { icon: "🎁", label: "Shop" } }); }
    setSuccess("");
    setError("");
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleSaveColors = async () => {
    if (!selectedOrg) return;
    const trimmedVideoUrl = bannerVideoUrl.trim();
    if (trimmedVideoUrl && !isHttpsUrl(trimmedVideoUrl)) {
      setError(t("customization.banner_video.invalid"));
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await organizationsService.update(selectedOrg.id, {
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        recognition_color: recognitionColor,
        nomination_color: nominationColor,
        welcome_title: welcomeTitle || null,
        welcome_message: welcomeMessage || null,
        banner_position: bannerPosition,
        banner_zoom: bannerZoom,
        banner_video_url: trimmedVideoUrl || null,
        app_name: appName || null,
        favicon_url: faviconUrl || null,
        avatar_color: avatarColor || null,
        widget_button_bg: widgetButtonBg || null,
        widget_panel_bg: widgetPanelBg || null,
        widget_header_bg: widgetHeaderBg || null,
        widget_header_text: widgetHeaderText || null,
        widget_nav_bg: widgetNavBg || null,
        widget_card_bg: widgetCardBg || null,
        widget_name_color: widgetNameColor || null,
        widget_nav_config: JSON.stringify(navConfig),
      });
      showSuccess(t("customization.success.saved"));
      setOrganizations(orgs => orgs.map(o =>
        o.id === selectedOrg.id ? { ...o, primary_color: primaryColor, secondary_color: secondaryColor, recognition_color: recognitionColor, nomination_color: nominationColor, welcome_title: welcomeTitle, welcome_message: welcomeMessage, banner_position: bannerPosition, banner_zoom: bannerZoom, banner_video_url: trimmedVideoUrl || null, app_name: appName, favicon_url: faviconUrl, avatar_color: avatarColor, widget_button_bg: widgetButtonBg, widget_panel_bg: widgetPanelBg, widget_header_bg: widgetHeaderBg, widget_header_text: widgetHeaderText, widget_nav_bg: widgetNavBg, widget_card_bg: widgetCardBg, widget_name_color: widgetNameColor, widget_nav_config: JSON.stringify(navConfig) } : o
      ));
    } catch {
      setError(t("customization.errors.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSubdomain = async () => {
    if (!selectedOrg) return;
    setSavingSubdomain(true);
    setSubdomainError("");
    setSuccess("");
    const cleaned = subdomain.trim().toLowerCase();
    try {
      const result = await organizationsService.updateSubdomain(
        selectedOrg.id,
        cleaned || null,
      );
      const newSub = result?.subdomain ?? null;
      setSubdomain(newSub || "");
      setOrganizations((orgs) =>
        orgs.map((o) =>
          o.id === selectedOrg.id ? { ...o, subdomain: newSub } : o,
        ),
      );
      showSuccess(
        newSub
          ? t("customization.success.subdomain_set", { subdomain: newSub })
          : t("customization.success.subdomain_cleared")
      );
    } catch (err) {
      setSubdomainError(err?.message || t("customization.errors.subdomain_save_failed"));
    } finally {
      setSavingSubdomain(false);
    }
  };

  const handleResetColors = async () => {
    if (!selectedOrg) return;
    setPrimaryColor(DEFAULT_PRIMARY);
    setSecondaryColor(DEFAULT_SECONDARY);
    setRecognitionColor(DEFAULT_RECOGNITION);
    setNominationColor(DEFAULT_NOMINATION);
    try {
      await organizationsService.update(selectedOrg.id, {
        primary_color: null,
        secondary_color: null,
        recognition_color: null,
        nomination_color: null,
      });
      showSuccess(t("customization.success.colors_reset"));
      setOrganizations(orgs => orgs.map(o =>
        o.id === selectedOrg.id ? { ...o, primary_color: null, secondary_color: null, recognition_color: null, nomination_color: null } : o
      ));
    } catch {
      setError(t("customization.errors.reset_colors_failed"));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOrg) return;
    try {
      const result = await organizationsService.uploadLogo(selectedOrg.id, file);
      setLogoPreview(result.url);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, logo_url: result.url } : o));
      await refreshUser();
      showSuccess(t("customization.success.logo_uploaded"));
    } catch {
      setError(t("customization.errors.upload_logo_failed"));
    }
  };

  const handleRemoveLogo = async () => {
    if (!selectedOrg) return;
    try {
      await organizationsService.deleteAsset(selectedOrg.id, "logo");
      setLogoPreview(null);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, logo_url: null } : o));
      await refreshUser();
      showSuccess(t("customization.success.logo_removed"));
    } catch {
      setError(t("customization.errors.remove_logo_failed"));
    }
  };

  const handleWidgetLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOrg) return;
    try {
      const result = await organizationsService.uploadWidgetLogo(selectedOrg.id, file);
      setWidgetLogoPreview(result.url);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, widget_logo_url: result.url } : o));
      await refreshUser();
      showSuccess(t("customization.success.widget_logo_uploaded"));
    } catch {
      setError(t("customization.errors.upload_widget_logo_failed"));
    }
  };

  const handleRemoveWidgetLogo = async () => {
    if (!selectedOrg) return;
    try {
      await organizationsService.deleteAsset(selectedOrg.id, "widget_logo");
      setWidgetLogoPreview(null);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, widget_logo_url: null } : o));
      showSuccess(t("customization.success.widget_logo_removed"));
    } catch {
      setError(t("customization.errors.remove_widget_logo_failed"));
    }
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOrg) return;
    try {
      const result = await organizationsService.uploadBackground(selectedOrg.id, file);
      setBgPreview(result.url);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, background_image_url: result.url } : o));
      showSuccess(t("customization.success.background_uploaded"));
    } catch {
      setError(t("customization.errors.upload_background_failed"));
    }
  };

  const handleRemoveBg = async () => {
    if (!selectedOrg) return;
    try {
      await organizationsService.deleteAsset(selectedOrg.id, "background");
      setBgPreview(null);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, background_image_url: null } : o));
      showSuccess(t("customization.success.background_removed"));
    } catch {
      setError(t("customization.errors.remove_background_failed"));
    }
  };

  const handleLoginLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOrg) return;
    try {
      const result = await organizationsService.uploadLoginLogo(selectedOrg.id, file);
      setLoginLogoPreview(result.url);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, login_logo_url: result.url } : o));
      showSuccess(t("customization.success.login_logo_uploaded"));
    } catch {
      setError(t("customization.errors.upload_login_logo_failed"));
    }
  };

  const handleRemoveLoginLogo = async () => {
    if (!selectedOrg) return;
    try {
      await organizationsService.deleteAsset(selectedOrg.id, "login_logo");
      setLoginLogoPreview(null);
      setOrganizations(orgs => orgs.map(o => o.id === selectedOrg.id ? { ...o, login_logo_url: null } : o));
      showSuccess(t("customization.success.login_logo_removed"));
    } catch {
      setError(t("customization.errors.remove_login_logo_failed"));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Organization Selector */}
      {!selectedOrg ? (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("customization.select_org_title")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {organizations.map((org) => (
              <motion.button
                key={org.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => selectOrg(org)}
                className="p-6 bg-white rounded-2xl shadow-sm border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all text-left"
              >
                <div className="flex items-center mb-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mr-3"
                    style={{ background: `linear-gradient(135deg, ${org.primary_color || DEFAULT_PRIMARY}, ${org.secondary_color || DEFAULT_SECONDARY})` }}
                  >
                    {org.name?.[0]?.toUpperCase() || 'O'}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{org.name}</h4>
                    <p className="text-xs text-gray-500">{org.description || t("customization.no_description")}</p>
                  </div>
                </div>
                {org.logo_url && (
                  <img src={org.logo_url} alt={t("customization.logo_alt")} className="h-6 w-auto mt-2 rounded" />
                )}
              </motion.button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {/* Back button */}
          <button
            onClick={() => setSelectedOrg(null)}
            className="mb-4 text-sm font-medium text-purple-600 hover:text-purple-500 flex items-center"
          >
            <X className="w-4 h-4 mr-1" /> {t("customization.back_to_organizations")}
          </button>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header with live preview */}
            <div
              className="p-6 text-white"
              style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
            >
              <div className="flex items-center">
                {logoPreview && (
                  <img src={logoPreview} alt={t("customization.logo_alt")} className="h-10 w-auto mr-3 rounded-full bg-white/20 p-1" />
                )}
                <div>
                  <h3 className="text-xl font-bold">{selectedOrg.name}</h3>
                  <p className="text-white/80 text-sm">{t("customization.live_preview")}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-10">
              {/* Success/Error Messages */}
              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-2xl flex items-center text-green-700">
                  <Check className="w-5 h-5 mr-2" />
                  <p className="text-sm">{success}</p>
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center text-red-700">
                  <X className="w-5 h-5 mr-2" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* ── IDENTITY ── */}
              <div className="space-y-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("customization.sections.identity")}</p>

                {/* App Name */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <MessageSquare className="w-4 h-4 mr-2" /> {t("customization.app_name.label")}
                  </h4>
                  <input
                    type="text"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    maxLength={100}
                    placeholder={t("customization.app_name.placeholder")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t("customization.app_name.hint")}</p>
                </div>

                {/* Subdomain */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <MessageSquare className="w-4 h-4 mr-2" /> {t("customization.subdomain.label")}
                  </h4>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={subdomain}
                      onChange={(e) => { setSubdomain(e.target.value); setSubdomainError(""); }}
                      maxLength={63}
                      placeholder={t("customization.subdomain.placeholder")}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-500 whitespace-nowrap">.{getDomainSuffix()}</span>
                    <button
                      type="button"
                      onClick={handleSaveSubdomain}
                      disabled={savingSubdomain}
                      className="px-4 py-2 rounded-full text-sm text-white org-gradient shadow-sm hover:shadow-md disabled:opacity-50"
                    >
                      {savingSubdomain ? t("customization.subdomain.saving") : t("common:actions.save")}
                    </button>
                  </div>
                  {subdomainError ? (
                    <p className="text-xs text-red-600 mt-1">{subdomainError}</p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">{t("customization.subdomain.hint")}</p>
                  )}
                </div>

                {/* Favicon */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <Image className="w-4 h-4 mr-2" /> {t("customization.favicon.label")}
                  </h4>
                  <div className="flex items-center gap-2">
                    {faviconUrl && <img src={faviconUrl} alt={t("customization.favicon.alt")} className="w-6 h-6 rounded" />}
                    <input
                      type="url"
                      value={faviconUrl}
                      onChange={(e) => setFaviconUrl(e.target.value)}
                      placeholder={t("customization.favicon.placeholder")}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{t("customization.favicon.hint")}</p>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* ── LOGOS ── */}
              <div className="space-y-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("customization.sections.logos")}</p>

                {/* Organization Logo */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-1">
                    <Upload className="w-4 h-4 mr-2" /> {t("customization.org_logo.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{t("customization.org_logo.hint")}</p>
                  <div className="flex items-center gap-3">
                    {logoPreview && <img src={logoPreview} alt={t("customization.org_logo.alt")} className="h-12 w-auto rounded-2xl border border-gray-200 p-1" />}
                    <label className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-all flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      {logoPreview ? t("customization.org_logo.change") : t("customization.org_logo.upload")}
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" />
                    </label>
                    {logoPreview && (
                      <button onClick={handleRemoveLogo} className="px-4 py-2 rounded-full border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-all flex items-center">
                        <Trash2 className="w-4 h-4 mr-2" /> {t("customization.remove")}
                      </button>
                    )}
                  </div>
                </div>

                {/* Login Page Logo */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-1">
                    <Upload className="w-4 h-4 mr-2" /> {t("customization.login_logo.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{t("customization.login_logo.hint")}</p>
                  <div className="flex items-center gap-3">
                    {loginLogoPreview && <img src={loginLogoPreview} alt={t("customization.login_logo.alt")} className="h-12 w-auto rounded-2xl border border-gray-200 p-1" />}
                    <label className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-all flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      {loginLogoPreview ? t("customization.org_logo.change") : t("customization.org_logo.upload")}
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLoginLogoUpload} className="hidden" />
                    </label>
                    {loginLogoPreview && (
                      <button onClick={handleRemoveLoginLogo} className="px-4 py-2 rounded-full border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-all flex items-center">
                        <Trash2 className="w-4 h-4 mr-2" /> {t("customization.remove")}
                      </button>
                    )}
                  </div>
                </div>

                {/* Widget Logo */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-1">
                    <Upload className="w-4 h-4 mr-2" /> {t("customization.widget_logo.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{t("customization.widget_logo.hint")}</p>
                  <div className="flex items-center gap-3">
                    {widgetLogoPreview && <img src={widgetLogoPreview} alt={t("customization.widget_logo.alt")} className="h-12 w-auto rounded-2xl border border-gray-200 p-1" />}
                    <label className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-all flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      {widgetLogoPreview ? t("customization.widget_logo.change") : t("customization.widget_logo.upload")}
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleWidgetLogoUpload} className="hidden" />
                    </label>
                    {widgetLogoPreview && (
                      <button onClick={handleRemoveWidgetLogo} className="px-4 py-2 rounded-full border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-all flex items-center">
                        <Trash2 className="w-4 h-4 mr-2" /> {t("customization.remove")}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* ── COLORS ── */}
              <div className="space-y-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("customization.sections.colors")}</p>

                {/* Theme Colors */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                      <Palette className="w-4 h-4 mr-2" /> {t("customization.theme_colors.label")}
                    </h4>
                    <button
                      onClick={handleResetColors}
                      className="text-xs text-gray-500 hover:text-purple-600 flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 hover:border-purple-300 transition-all"
                    >
                      <RotateCcw className="w-3 h-3" /> {t("customization.theme_colors.reset")}
                    </button>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">{t("customization.theme_colors.primary")}</label>
                      <div className="flex items-center gap-2">
                        <label className="relative w-10 h-10 rounded-full border-2 border-gray-200 cursor-pointer shadow-sm hover:scale-105 transition-transform" style={{ backgroundColor: primaryColor }}>
                          <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                        </label>
                        <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-24 px-3 py-1.5 border border-gray-300 rounded-full text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">{t("customization.theme_colors.secondary")}</label>
                      <div className="flex items-center gap-2">
                        <label className="relative w-10 h-10 rounded-full border-2 border-gray-200 cursor-pointer shadow-sm hover:scale-105 transition-transform" style={{ backgroundColor: secondaryColor }}>
                          <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                        </label>
                        <input type="text" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="w-24 px-3 py-1.5 border border-gray-300 rounded-full text-sm" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Colors */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <Palette className="w-4 h-4 mr-2" /> {t("customization.feature_colors.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">{t("customization.feature_colors.hint")}</p>
                  <div className="flex gap-6">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">{t("customization.feature_colors.recognition")}</label>
                      <div className="flex items-center gap-2">
                        <label className="relative w-10 h-10 rounded-full border-2 border-gray-200 cursor-pointer shadow-sm hover:scale-105 transition-transform" style={{ backgroundColor: recognitionColor }}>
                          <input type="color" value={recognitionColor} onChange={(e) => setRecognitionColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                        </label>
                        <input type="text" value={recognitionColor} onChange={(e) => setRecognitionColor(e.target.value)} className="w-24 px-3 py-1.5 border border-gray-300 rounded-full text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">{t("customization.feature_colors.nomination")}</label>
                      <div className="flex items-center gap-2">
                        <label className="relative w-10 h-10 rounded-full border-2 border-gray-200 cursor-pointer shadow-sm hover:scale-105 transition-transform" style={{ backgroundColor: nominationColor }}>
                          <input type="color" value={nominationColor} onChange={(e) => setNominationColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                        </label>
                        <input type="text" value={nominationColor} onChange={(e) => setNominationColor(e.target.value)} className="w-24 px-3 py-1.5 border border-gray-300 rounded-full text-sm" />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full" style={{ backgroundColor: recognitionColor }}></div>
                      <span className="text-xs text-gray-500">{t("customization.feature_colors.recognition_accent")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full" style={{ backgroundColor: nominationColor }}></div>
                      <span className="text-xs text-gray-500">{t("customization.feature_colors.nomination_accent")}</span>
                    </div>
                  </div>
                </div>

                {/* Avatar Color */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <Palette className="w-4 h-4 mr-2" /> {t("customization.avatar_color.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">{t("customization.avatar_color.hint")}</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <label className="relative w-10 h-10 rounded-full border-2 border-gray-200 cursor-pointer shadow-sm hover:scale-105 transition-transform" style={{ backgroundColor: avatarColor || '#7C3AED' }}>
                        <input type="color" value={avatarColor || '#7C3AED'} onChange={(e) => setAvatarColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                      </label>
                      <input type="text" value={avatarColor} onChange={(e) => setAvatarColor(e.target.value)} placeholder="#7C3AED" className="w-24 px-3 py-1.5 border border-gray-300 rounded-full text-sm" />
                    </div>
                    {avatarColor && <button onClick={() => setAvatarColor("")} className="text-xs text-gray-500 hover:text-red-500">{t("customization.clear")}</button>}
                    <div className="flex items-center gap-2 ml-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: avatarColor ? `linear-gradient(135deg, ${avatarColor}, ${avatarColor}dd)` : 'linear-gradient(135deg, #7C3AED, #4F46E5)' }}>JD</div>
                      <span className="text-xs text-gray-500">{t("customization.avatar_color.preview")}</span>
                    </div>
                  </div>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* ── HOME FEED ── */}
              <div className="space-y-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("customization.sections.home_feed")}</p>

                {/* Banner Image */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-1">
                    <Image className="w-4 h-4 mr-2" /> {t("customization.banner.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{t("customization.banner.hint")}</p>
                  <div className="flex items-center gap-3">
                    {bgPreview && <img src={bgPreview} alt={t("customization.banner.alt")} className="h-16 w-24 object-cover rounded-2xl border border-gray-200" />}
                    <label className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-all flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      {bgPreview ? t("customization.banner.change") : t("customization.banner.upload")}
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleBgUpload} className="hidden" />
                    </label>
                    {bgPreview && (
                      <button onClick={handleRemoveBg} className="px-4 py-2 rounded-full border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-all flex items-center">
                        <Trash2 className="w-4 h-4 mr-2" /> {t("customization.remove")}
                      </button>
                    )}
                  </div>
                </div>

                {/* Banner Video */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-1">
                    <Video className="w-4 h-4 mr-2" /> {t("customization.banner_video.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{t("customization.banner_video.hint")}</p>
                  <div className="flex items-center gap-3">
                    <input
                      type="url"
                      value={bannerVideoUrl}
                      onChange={(e) => setBannerVideoUrl(e.target.value)}
                      placeholder="https://cdn.example.com/banner.mp4"
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    {bannerVideoUrl && (
                      <button onClick={() => setBannerVideoUrl("")} className="px-4 py-2 rounded-full border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-all flex items-center flex-shrink-0">
                        <Trash2 className="w-4 h-4 mr-2" /> {t("customization.remove")}
                      </button>
                    )}
                  </div>
                  {bannerVideoUrl.trim() && !isHttpsUrl(bannerVideoUrl.trim()) && (
                    <p className="text-xs text-red-600 mt-1">{t("customization.banner_video.invalid")}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{t("customization.banner_video.save_note")}</p>
                </div>

                {/* Banner Position */}
                {(bgPreview || isHttpsUrl(bannerVideoUrl.trim())) && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                      <Image className="w-4 h-4 mr-2" /> {t("customization.banner_position.label")}
                    </h4>
                    <p className="text-xs text-gray-500 mb-2">{t("customization.banner_position.hint")}</p>
                    <div className="space-y-3">
                      <div className="relative h-28 rounded-2xl overflow-hidden border border-gray-200 bg-gray-50">
                        <BannerMedia
                          imageUrl={bgPreview}
                          videoUrl={isHttpsUrl(bannerVideoUrl.trim()) ? bannerVideoUrl.trim() : null}
                          position={bannerPosition}
                          zoom={bannerZoom}
                          alt={t("customization.banner_position.alt")}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-8">{t("customization.banner_position.top")}</span>
                        <input type="range" min="0" max="100" value={bannerPosition} onChange={(e) => setBannerPosition(parseInt(e.target.value))} className="flex-1 accent-purple-600" />
                        <span className="text-xs text-gray-500 w-12">{t("customization.banner_position.bottom")}</span>
                        <span className="text-xs font-medium text-gray-700 w-8 text-right">{bannerPosition}%</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{t("customization.banner_zoom.label")}</p>
                        <p className="text-xs text-gray-500 mb-2">{t("customization.banner_zoom.hint")}</p>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 w-20">{t("customization.banner_zoom.whole")}</span>
                          <input type="range" min="0" max="100" value={bannerZoom} onChange={(e) => setBannerZoom(parseInt(e.target.value))} className="flex-1 accent-purple-600" aria-label={t("customization.banner_zoom.label")} />
                          <span className="text-xs text-gray-500 w-12">{t("customization.banner_zoom.fill")}</span>
                          <span className="text-xs font-medium text-gray-700 w-8 text-right">{bannerZoom}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Welcome Message */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <MessageSquare className="w-4 h-4 mr-2" /> {t("customization.welcome.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{t("customization.welcome.hint")}</p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">{t("customization.welcome.title_label")}</label>
                      <input
                        type="text"
                        value={welcomeTitle}
                        onChange={(e) => setWelcomeTitle(e.target.value)}
                        maxLength={200}
                        placeholder={t("customization.welcome.title_placeholder")}
                        className="w-full px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">{welcomeTitle.length}/200</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">{t("customization.welcome.subtitle_label")}</label>
                      <textarea
                        value={welcomeMessage}
                        onChange={(e) => setWelcomeMessage(e.target.value)}
                        maxLength={500}
                        rows={2}
                        placeholder={t("customization.welcome.subtitle_placeholder")}
                        className="w-full px-4 py-3 border border-gray-300 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">{welcomeMessage.length}/500</p>
                    </div>
                  </div>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* ── WIDGET ── */}
              <div className="space-y-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("customization.sections.widget")}</p>

                {/* Widget Colors */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <Palette className="w-4 h-4 mr-2" /> {t("customization.widget_colors.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">{t("customization.widget_colors.hint")}</p>
                  <div className="flex gap-6 flex-wrap">
                    {[
                      { id: "button", label: t("customization.widget_colors.button_closed"), value: widgetButtonBg, setter: setWidgetButtonBg, placeholder: "#FFFFFF" },
                      { id: "panel", label: t("customization.widget_colors.panel_opened"), value: widgetPanelBg, setter: setWidgetPanelBg, placeholder: "#FFFFFF" },
                      { id: "header_bg", label: t("customization.widget_colors.header_background"), value: widgetHeaderBg, setter: setWidgetHeaderBg, placeholder: "#FFFFFF" },
                      { id: "header_text", label: t("customization.widget_colors.header_text"), value: widgetHeaderText, setter: setWidgetHeaderText, placeholder: "#1C1E21" },
                      { id: "nav", label: t("customization.widget_colors.nav_bar"), value: widgetNavBg, setter: setWidgetNavBg, placeholder: "#f8f9fa" },
                      { id: "card", label: t("customization.widget_colors.card_background"), value: widgetCardBg, setter: setWidgetCardBg, placeholder: "#FFFFFF" },
                      { id: "name", label: t("customization.widget_colors.name_highlight"), value: widgetNameColor, setter: setWidgetNameColor, placeholder: "#7c3aed" },
                    ].map(({ id, label, value, setter, placeholder }) => (
                      <div key={id}>
                        <label className="text-xs text-gray-500 block mb-1">{label}</label>
                        <div className="flex items-center gap-2">
                          <label className="relative w-10 h-10 rounded-xl border-2 border-gray-200 cursor-pointer shadow-sm hover:scale-105 transition-transform" style={{ backgroundColor: value || placeholder }}>
                            <input type="color" value={value || placeholder} onChange={(e) => setter(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                          </label>
                          <input type="text" value={value} onChange={(e) => setter(e.target.value)} placeholder={placeholder} className="w-24 px-3 py-1.5 border border-gray-300 rounded-full text-sm" />
                          {value && <button onClick={() => setter("")} className="text-xs text-gray-500 hover:text-red-500">{t("customization.clear")}</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Widget Tab Icons & Labels */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                    <Palette className="w-4 h-4 mr-2" /> {t("customization.widget_tabs.label")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">{t("customization.widget_tabs.hint")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {["home", "recognitions", "nominations", "marketplace"].map(key => {
                      const icon = navConfig[key]?.icon || "";
                      const isImageIcon = icon.startsWith("http") || icon.startsWith("data:") || icon.startsWith("/api/");
                      return (
                        <div key={key} className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                          <span className="text-xs font-medium text-gray-600 capitalize block mb-2">{widgetTabNames[key] || key}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded border border-gray-300 bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {isImageIcon ? <img src={icon} alt="" className="w-6 h-6 object-contain" /> : <span className="text-lg">{icon || "?"}</span>}
                            </div>
                            <input
                              type="text"
                              value={isImageIcon ? "" : icon}
                              onChange={(e) => setNavConfig(prev => ({ ...prev, [key]: { ...prev[key], icon: e.target.value } }))}
                              className="w-14 px-1 py-1 border border-gray-300 rounded text-center text-lg"
                              maxLength={4}
                              placeholder="🏠"
                              title={t("customization.widget_tabs.emoji_title")}
                            />
                            <label className="px-2 py-1 rounded border border-gray-300 text-xs text-gray-600 hover:bg-gray-100 cursor-pointer flex-shrink-0" title={t("customization.widget_tabs.upload_icon_title")}>
                              <Upload className="w-3 h-3 inline mr-1" />{t("customization.widget_tabs.upload_icon_label")}
                              <input type="file" accept="image/png,image/svg+xml,image/webp" className="hidden" onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = () => setNavConfig(prev => ({ ...prev, [key]: { ...prev[key], icon: reader.result } }));
                                reader.readAsDataURL(file);
                              }} />
                            </label>
                            {isImageIcon && (
                              <button onClick={() => setNavConfig(prev => ({ ...prev, [key]: { ...prev[key], icon: "" } }))} className="text-xs text-red-500 hover:text-red-700" title={t("customization.widget_tabs.remove_image_title")}>✕</button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={navConfig[key]?.label || ""}
                            onChange={(e) => setNavConfig(prev => ({ ...prev, [key]: { ...prev[key], label: e.target.value } }))}
                            className="w-full mt-2 px-2 py-1 border border-gray-300 rounded text-sm"
                            maxLength={20}
                            placeholder={widgetTabNames[key] || key}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveColors}
                disabled={saving}
                className={`w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all ${
                  saving
                    ? "bg-gray-400"
                    : "bg-gradient-to-r hover:shadow-lg shadow-md"
                }`}
                style={!saving ? { background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` } : undefined}
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="w-5 h-5 mr-2" />
                )}
                {saving ? t("customization.saving") : t("customization.save_customization")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizationSection;
